import {
    AffiliateStatus,
    CommissionKind,
    CommissionStatus,
    PayoutBlockedReason,
    PayoutStatus,
    ReferralStatus,
} from 'src/types/Affiliate'
import { formatCents } from 'src/utils/affiliateFormat'
import { getReason } from 'src/utils/apiReason'
import { parseError } from 'src/utils/parseError'
import { Badge, BadgeTone } from './ui'

const affiliateStatus: Record<AffiliateStatus, [string, BadgeTone]> = {
    applied: ['Applied', 'yellow'],
    active: ['Active', 'green'],
    paused: ['Paused', 'yellow'],
    rejected: ['Rejected', 'red'],
    terminated: ['Terminated', 'red'],
}

const commissionKind: Record<CommissionKind, [string, BadgeTone]> = {
    first: ['First month', 'pink'],
    recurring: ['Recurring', 'blue'],
    clawback: ['Clawback', 'red'],
    adjustment: ['Adjustment', 'neutral'],
}

const commissionStatus: Record<CommissionStatus, [string, BadgeTone]> = {
    pending: ['Pending', 'yellow'],
    available: ['Available', 'green'],
    in_flight: ['Paying out', 'blue'],
    paid: ['Paid', 'neutral'],
    reversed: ['Reversed', 'red'],
}

const referralStatus: Record<ReferralStatus, [string, BadgeTone]> = {
    trialing: ['Trialing', 'blue'],
    active: ['Active', 'green'],
    cancelling: ['Cancelling', 'yellow'],
    churned: ['Churned', 'red'],
    never_paid: ['Never paid', 'neutral'],
}

const payoutStatus: Record<PayoutStatus, [string, BadgeTone]> = {
    requested: ['Awaiting approval', 'yellow'],
    processing: ['Processing', 'blue'],
    paid: ['Paid', 'green'],
    partially_paid: ['Partially paid', 'yellow'],
    failed: ['Failed', 'red'],
    rejected: ['Rejected', 'red'],
}

const badge = ([label, tone]: [string, BadgeTone]) => <Badge $tone={tone}>{label}</Badge>

export const AffiliateStatusBadge = ({ status }: { status: AffiliateStatus }) => badge(affiliateStatus[status])
export const CommissionKindBadge = ({ kind }: { kind: CommissionKind }) => badge(commissionKind[kind])
export const CommissionStatusBadge = ({ status }: { status: CommissionStatus }) => badge(commissionStatus[status])
export const ReferralStatusBadge = ({ status }: { status: ReferralStatus }) => badge(referralStatus[status])
export const PayoutStatusBadge = ({ status }: { status: PayoutStatus }) => badge(payoutStatus[status])

export const AFFILIATE_STATUS_OPTIONS = Object.entries(affiliateStatus).map(([value, [label]]) => ({
    value: value as AffiliateStatus,
    label,
}))

export const COMMISSION_STATUS_OPTIONS = Object.entries(commissionStatus).map(([value, [label]]) => ({
    value: value as CommissionStatus,
    label,
}))

export const REFERRAL_STATUS_OPTIONS = Object.entries(referralStatus).map(([value, [label]]) => ({
    value: value as ReferralStatus,
    label,
}))

export const PAYOUT_STATUS_OPTIONS = Object.entries(payoutStatus).map(([value, [label]]) => ({
    value: value as PayoutStatus,
    label,
}))

/** Why the Request payout button is disabled, in the affiliate's words. */
export const payoutBlockedText = (reason: PayoutBlockedReason, amountToThresholdCents = 0, minPayoutCents = 0) => {
    switch (reason) {
        case 'terms':
            return 'Accept the updated affiliate terms to request a payout.'
        case 'connect':
            return 'Set up payouts with Stripe first.'
        case 'below_threshold':
            return `${formatCents(amountToThresholdCents)} more to reach the ${formatCents(minPayoutCents)} minimum.`
        case 'negative_balance':
            return 'Your balance is negative after a refund or chargeback. Future earnings will offset it.'
        case 'in_flight':
            return 'A payout is already on its way.'
        case 'paused':
            return 'Payouts are disabled while your account is paused.'
        case 'not_active':
            return 'Payouts are not available for this account.'
        default:
            return ''
    }
}

/** Human text for the stable error `reason` slugs the affiliate API returns. */
export const reasonText: Record<string, string> = {
    already_affiliate: "You're already in the affiliate program.",
    terms_outdated: 'The terms were just updated. Please review the new version and accept again.',
    terms_not_accepted: 'Accept the current affiliate terms first.',
    applications_closed: 'Applications are closed right now.',
    reapply_cooldown: "You can't re-apply yet.",
    terminated: 'This account was removed from the affiliate program.',
    invite_invalid: 'This invite link is not valid.',
    invite_expired: 'This invite link has expired.',
    invite_used: 'This invite has already been used.',
    invite_email_mismatch: 'This invite was sent to a different email address.',
    code_taken: 'That code is already taken.',
    code_invalid: 'Codes are 3–20 characters: lowercase letters, numbers and dashes.',
    connect_not_enabled: 'Finish setting up payouts with Stripe first.',
    below_threshold: "Your available balance hasn't reached the payout minimum yet.",
    negative_balance: 'Your balance is negative, so there is nothing to pay out yet.',
    payout_in_progress: 'A payout is already in progress.',
    affiliate_status: "That's not available for your account right now.",
    not_affiliate: "You're not in the affiliate program.",
    not_held: 'This commission is no longer held for review.',
    payout_not_requested: 'Only payouts awaiting approval can be approved.',
    user_not_found: 'No user found with that id.',
}

/** The message to show for a failed affiliate API call. */
export const errorMessage = (error: unknown) => {
    const reason = getReason(error)
    return (reason && reasonText[reason]) || parseError(error) || 'Something went wrong. Please try again.'
}
